<template>
  <div>
    <div class="container pt-3" style="min-height: auto;">
      <div class="row">
        <div class="col-12">
          <!-- 
                PROPS:
                - office-logo-url: string
            -->
          <report-header :office-logo-url="officeLogo"></report-header>
        </div>
        <div class="col-12">
          <!-- PROPS:
             - display-address: string
             - bed: string | number
             - bath: string | number
             - parking: string | number
             - report-date: string
             - campaign-length: string
             - campaign-start: string
             - campaign-package: string
             - media-channels: string
             -->
          <property-overview-card
            :display-address="campaignData.property.title"
            :bed="campaignData.property.beds"
            :bath="campaignData.property.baths"
            :parking="campaignData.property.carbays"
            :report-date="formatDate(new Date())"
            :campaign-length="campaignData.package.duration + ' days'"
            :campaign-start="formatDate(campaignData.startDate)"
            :campaign-package="campaignData.package.title"
            media-channels="Social, Display, & Native"
          ></property-overview-card>
        </div>
      </div>
      <div class="row report__uneven-columns">
        <div
          class="col-6 report__uneven-column"
          :style="{paddingRight: colGridGapOverride.paddingRight}"
        >
          <!-- PROPS:
             - agent-title: string
             - agent-number: string
             - agent-email: string
             -->
          <agent-card
            layout="horizontal"
            :agent-name="campaignData.agentName"
            :agent-title="`${campaignData.officeName}`"
            :agent-number="campaignData.agentPhone"
            :agent-email="campaignData.agentEmail"
            :agent-image-url="agentImage"
          ></agent-card>
        </div>
        <div
          class="col-6 report__uneven-column"
          :style="{paddingLeft: colGridGapOverride.paddingLeft}"
        >
          <title-card text="Finding buyers beyond the real estate sites">
            <template v-slot:title-component>
              <default-card-title-component
                text="Finding buyers beyond the real estate sites"
              ></default-card-title-component>
            </template>
          </title-card>
        </div>
      </div>
      <div class="row report__uneven-columns" v-if="showMetrics">
        <div
          class="col-4 report__uneven-column"
          :style="{paddingRight: colGridGapOverride.paddingRight}"
        >
          <!-- PROPS:
             - metric: string
             -->
          <awareness-card
            :impressions-metric="impressionsMetric"
            label="Total number of times your ad was show to potential buyers"
          ></awareness-card>
        </div>
        <div
          class="col-8 report__uneven-column"
          :style="{paddingLeft: colGridGapOverride.paddingLeft}"
        >
          <interest-and-engagement-card
            :click-metric="clicksMetric"
            :social-ad-metric="socialAdMetric"
          ></interest-and-engagement-card>
        </div>
      </div>
      <div class="row" v-show="showInterestIndicators">
        <div class="col-12">
          <!-- 
            PROPS:
                - soi-downloads: string | number,
                - image-views: string | number,
                - video-views: string | number,
                - floorplan-views: string | number,
                - inspection-auction-location-views: string | number,
            -->
          <interest-indicators-card
            :soi-downloads="soiDownloads"
            :image-views="imageViews"
            :video-views="videoViews"
            :floorplan-views="floorplanViews"
            :inspection-auction-location-views="inspectionAuctionLocationViews"
            :website-live-chat="websiteLiveChat"
          ></interest-indicators-card>
        </div>
      </div>
      <div class="row" v-show="showEnquiry">
        <div class="col-12">
          <!-- 
            PROPS:
                - email-metric: string
                - phone-metric: string
                - form-metric: string
            -->
          <enquiry-card
            :email-metric="enquiryViaEmail"
            :phone-metric="enquiryViaPhone"
            :form-metric="enquiryViaForm"
            :social-metric="enquiryViaSocial"
          ></enquiry-card>
        </div>
      </div>
      <report-footer></report-footer>
    </div>
  </div>
</template>

<script>
import ReportHeader from '../ReportComponents/Common/ReportHeader.vue';
import PropertyOverviewCard from '../ReportComponents/Cards/PropertyOverviewCard.vue';
import AgentCard from '../ReportComponents/Cards/AgentCard.vue';
import TitleCard from '../ReportComponents/Cards/TitleCard.vue';
import DefaultCardTitleComponent from '../ReportComponents/Common/DefaultCardTitleComponent.vue';
import {COL_HORIZONTAL_PADDING_OVERRIDE} from '../ReportComponents/constants';
import AwarenessCard from '../ReportComponents/Cards/AwarenessCard.vue';
import InterestAndEngagementCard from '../ReportComponents/Cards/InterestAndEngagementCard.vue';
import EnquiryCard from '../ReportComponents/Cards/EnquiryCard.vue';
import InterestIndicatorsCard from '../ReportComponents/Cards/InterestIndicatorsCard.vue';
import ReportFooter from '../ReportComponents/Common/ReportFooter.vue';
import moment from 'moment';

export default {
  name: 'PropertyPdfV2',
  props: {
    campaignData: {
      type: Object,
      required: true,
    },
  },

  components: {
    ReportHeader,
    PropertyOverviewCard,
    AgentCard,
    TitleCard,
    DefaultCardTitleComponent,
    AwarenessCard,
    InterestAndEngagementCard,
    EnquiryCard,
    InterestIndicatorsCard,
    ReportFooter,
  },
  computed: {
    colGridGapOverride() {
      return {
        paddingRight: COL_HORIZONTAL_PADDING_OVERRIDE,
        paddingLeft: COL_HORIZONTAL_PADDING_OVERRIDE,
      };
    },
    impressionsMetric() {
      return this.campaignData.stats
        ? Number(this.campaignData.stats.impressions).toLocaleString()
        : 0;
    },
    interestAndEngagementMetric() {
      return this.campaignData.stats
        ? Number(this.campaignData.stats.interestAndEngagement).toLocaleString()
        : 0;
    },
    clicksMetric() {
      return this.campaignData.stats
        ? Number(this.campaignData.stats.clicks).toLocaleString()
        : 0;
    },
    socialAdMetric() {
      return this.campaignData.stats
        ? Number(
            this.campaignData.stats['conversions']['socialEngagement']
          ).toLocaleString()
        : 0;
    },
    showMetrics() {
      const hasClickMetric =
        this.clicksMetric !== 0 && this.clicksMetric !== '0';
      const hasSocialAdMetric =
        this.socialAdMetric !== 0 || this.socialAdMetric === '0';
      const hasImpressionMetric =
        this.impressionsMetric !== 0 && this.impressionsMetric !== '0';
      return hasClickMetric || hasSocialAdMetric || hasImpressionMetric;
    },
    soiDownloads() {
      if (
        this.campaignData &&
        this.campaignData.stats &&
        this.campaignData.stats.conversions
      ) {
        return (
          this.campaignData.stats.conversions['SOI'] || 0
        ).toLocaleString();
      }
      return 0;
    },
    imageViews() {
      if (
        this.campaignData &&
        this.campaignData.stats &&
        this.campaignData.stats.conversions
      ) {
        return (
          this.campaignData.stats.conversions['ViewGallery'] ||
          this.campaignData.stats.conversions['ImageCarouselClick'] ||
          0
        ).toLocaleString();
      }
      return 0;
    },
    videoViews() {
      if (
        this.campaignData &&
        this.campaignData.stats &&
        this.campaignData.stats.conversions
      ) {
        return (
          this.campaignData.stats.conversions['VideoView'] ||
          this.campaignData.stats.conversions['ListingVideo'] ||
          this.campaignData.stats.conversions['ViewVideo'] ||
          this.campaignData.stats.videoViews ||
          0
        ).toLocaleString();
      }
      return 0;
    },
    floorplanViews() {
      if (
        this.campaignData &&
        this.campaignData.stats &&
        this.campaignData.stats.conversions
      ) {
        return (
          this.campaignData.stats.conversions['ViewFloorplan'] ||
          this.campaignData.stats.conversions['FloorplanbtnClick'] ||
          this.campaignData.stats.conversions['ListingFloorplan'] ||
          0
        ).toLocaleString();
      }
      return 0;
    },
    inspectionAuctionLocationViews() {
      if (
        this.campaignData &&
        this.campaignData.stats &&
        this.campaignData.stats.conversions
      ) {
        return (
          (this.campaignData.stats.conversions['AddToCalendar'] || 0) +
          (this.campaignData.stats.conversions['ListingMap'] || 0) +
          (this.campaignData.stats.conversions['GetDirectionClick'] ||
            this.campaignData.stats.conversions['GetDirections'] ||
            0)
        ).toLocaleString();
      }
      return 0;
    },
    websiteLiveChat() {
      if (
        this.campaignData &&
        this.campaignData.stats &&
        this.campaignData.stats.conversions
      ) {
        return (
          this.campaignData.stats.conversions['InitChat'] || 0
        ).toLocaleString();
      }
      return 0;
    },
    showInterestIndicators() {
      const hasInspectionAuctionLocationViews =
        this.inspectionAuctionLocationViews !== '0' &&
        this.inspectionAuctionLocationViews !== 0;
      const hasFloorplanViews =
        this.floorplanViews !== '0' && this.floorplanViews !== 0;
      const hasVideoViews = this.videoViews !== '0' && this.videoViews !== 0;
      const hasImageViews = this.imageViews !== '0' && this.imageViews !== 0;
      const hasSoiDownloads =
        this.soiDownloads !== '0' && this.soiDownloads !== 0;
      const hasWebsiteLiveChat =
        this.websiteLiveChat !== '0' && this.websiteLiveChat !== 0;

      return (
        hasInspectionAuctionLocationViews ||
        hasFloorplanViews ||
        hasVideoViews ||
        hasImageViews ||
        hasSoiDownloads ||
        hasWebsiteLiveChat
      );
    },
    enquiryViaEmail() {
      if (
        this.campaignData &&
        this.campaignData.stats &&
        this.campaignData.stats.conversions
      ) {
        return (
          this.campaignData.stats.conversions['EmailClick'] ||
          this.campaignData.stats.conversions['AgentEmail'] ||
          0
        ).toLocaleString();
      }
      return 0;
    },
    enquiryViaPhone() {
      if (
        this.campaignData &&
        this.campaignData.stats &&
        this.campaignData.stats.conversions
      ) {
        return (
          this.campaignData.stats.conversions['ClickToCall'] ||
          this.campaignData.stats.conversions['Click-To-Call'] ||
          this.campaignData.stats.conversions['PhoneNumberClick'] ||
          0
        ).toLocaleString();
      }
      return 0;
    },
    enquiryViaForm() {
      if (
        this.campaignData &&
        this.campaignData.stats &&
        this.campaignData.stats.conversions
      ) {
        return (
          this.campaignData.stats.conversions['enquireForm'] || 0
        ).toLocaleString();
      }
      return 0;
    },
    enquiryViaSocial() {
      if (
        this.campaignData &&
        this.campaignData.stats &&
        this.campaignData.stats.conversions
      ) {
        return (
          this.campaignData.stats.conversions['SocialFormLeads'] || 0
        ).toLocaleString();
      }
      return 0;
    },
    officeLogo() {
      if (
        this.campaignData &&
        this.campaignData.agency &&
        this.campaignData.agency.imageLogo &&
        this.campaignData.agency.imageLogo.location
      ) {
        return (
          `${process.env.VUE_APP_CDN_URL}${
            this.campaignData.agency.imageLogo.location
          }?x-request=` + Math.floor(Math.random() * 1000000)
        );
      }
      return null;
    },
    agentImage() {
      if (
        this.campaignData &&
        this.campaignData.agentImage &&
        this.campaignData.agentImage.location
      ) {
        return (
          `${process.env.VUE_APP_CDN_URL}${
            this.campaignData.agentImage.location
          }?x-request=` + Math.floor(Math.random() * 1000000)
        );
      }
      return this.campaignData.productResponse.Data.CustomField7;
    },
    showEnquiry() {
      return (
        this.enquiryViaEmail || this.enquiryViaPhone || this.enquiryViaForm
      );
    },
  },
  methods: {
    formatDate(date) {
      return moment(date).format('DD MMMM YYYY');
    },
  },
};
</script>

<style scoped>
.report__uneven-columns > .report__uneven-column > .report__card-container {
  height: calc(100% - 12px) !important;
}

.report__uneven-columns > .report__uneven-column:first-of-type {
  padding-right: 6px;
}

.report__uneven-columns > .report__uneven-column:last-of-type {
  padding-left: 6px;
}
</style>
